import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
    Plus, Edit2, Trash2, CheckCircle, Loader2, AlertTriangle,
    Clock, Image, Upload, X, ChevronDown, ChevronUp, Filter
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import api from '../../services/api';
import { RequestTypeLabel, DocumentTypeCode, DocumentReferenceType } from '../../constants/stageConstants';

const PRIORITY_COLORS = {
    low: 'bg-gray-100 text-gray-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    urgent: 'bg-red-100 text-red-700',
};

const STATUS_COLORS = {
    open: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    closed: 'bg-gray-100 text-gray-800',
};

const STATUS_LABEL = {
    open: 'Open',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    rejected: 'Rejected',
    closed: 'Closed',
};

const TYPE_OPTIONS = [
    { value: 'discrepancy', label: 'Discrepancy' },
    { value: 'extra_work', label: 'Extra Work' },
    { value: 'team_request', label: 'Team Request' },
    { value: 'timeline_change', label: 'Timeline Change' },
    { value: 'material_issue', label: 'Material Issue' },
    { value: 'design_change', label: 'Design Change' },
    { value: 'other', label: 'Other' },
];

const PRIORITY_OPTIONS = [
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
];

const ProjectRequests = ({ projectId, isProjectCompleted = false }) => {
    const { user } = useAuth();
    const isOEM = user?.role === 'oem';

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState(null);

    // Filters
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    // Create / Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);
    const [formData, setFormData] = useState({
        requestType: 'discrepancy',
        title: '',
        description: '',
        priority: 'medium',
        proposedCompletionDate: '',
        extendedHoursStart: '',
        extendedHoursEnd: '',
        requestedTeamMembers: '',
    });
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Close dialog
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [closingRequest, setClosingRequest] = useState(null);
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [closing, setClosing] = useState(false);

    // Delete
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingRequest, setDeletingRequest] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filterType) params.type = filterType;
            if (filterStatus) params.status = filterStatus;
            const data = await api.getProjectRequests(projectId, params);
            setRequests(data || []);
        } catch (err) {
            console.error('Failed to fetch requests:', err);
            toast({ title: 'Error', description: 'Failed to load requests', variant: 'destructive' });
        } finally {
            setLoading(false);
        }
    }, [projectId, filterType, filterStatus]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    // ── Create / Edit ──

    const openCreateDialog = () => {
        setEditingRequest(null);
        setFormData({
            requestType: 'discrepancy', title: '', description: '', priority: 'medium',
            proposedCompletionDate: '', extendedHoursStart: '', extendedHoursEnd: '', requestedTeamMembers: '',
        });
        setSelectedFiles([]);
        setDialogOpen(true);
    };

    const openEditDialog = (req) => {
        setEditingRequest(req);
        setFormData({
            requestType: req.requestType,
            title: req.title,
            description: req.description,
            priority: req.priority,
            proposedCompletionDate: req.proposedCompletionDate ? req.proposedCompletionDate.split('T')[0] : '',
            extendedHoursStart: req.extendedHoursStart || '',
            extendedHoursEnd: req.extendedHoursEnd || '',
            requestedTeamMembers: req.requestedTeamMembers || '',
        });
        setSelectedFiles([]);
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!formData.title.trim() || !formData.description.trim()) {
            toast({ title: 'Validation', description: 'Title and description are required', variant: 'destructive' });
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                requestType: formData.requestType,
                title: formData.title.trim(),
                description: formData.description.trim(),
                priority: formData.priority,
            };

            // OEM fields
            if (isOEM) {
                if (formData.proposedCompletionDate) payload.proposedCompletionDate = formData.proposedCompletionDate;
                if (formData.extendedHoursStart) payload.extendedHoursStart = formData.extendedHoursStart;
                if (formData.extendedHoursEnd) payload.extendedHoursEnd = formData.extendedHoursEnd;
                if (formData.requestedTeamMembers) payload.requestedTeamMembers = formData.requestedTeamMembers;
            }

            let requestId;
            if (editingRequest) {
                await api.updateProjectRequest(projectId, editingRequest.id, payload);
                requestId = editingRequest.id;
                toast({ title: 'Updated', description: 'Request updated successfully' });
            } else {
                const result = await api.createProjectRequest(projectId, payload);
                requestId = result.id;
                toast({ title: 'Created', description: `Request ${result.requestNumber} created` });
            }

            // Upload photos if any
            if (selectedFiles.length > 0 && requestId) {
                for (const file of selectedFiles) {
                    const fd = new FormData();
                    fd.append('file', file);
                    fd.append('documentTypeCode', DocumentTypeCode.DiscrepancyDoc);
                    fd.append('subfolder', 'requests');
                    fd.append('referenceType', DocumentReferenceType.Request);
                    fd.append('referenceId', requestId);

                    const token = localStorage.getItem('token');
                    await fetch(`${api.baseUrl}/api/projects/${projectId}/documents/upload`, {
                        method: 'POST',
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                        body: fd,
                    });
                }
            }

            setDialogOpen(false);
            fetchRequests();
        } catch (err) {
            toast({ title: 'Error', description: err.message || 'Failed to save request', variant: 'destructive' });
        } finally {
            setSubmitting(false);
        }
    };

    // ── Close ──

    const handleClose = async () => {
        if (!closingRequest) return;
        setClosing(true);
        try {
            await api.closeProjectRequest(projectId, closingRequest.id, { resolutionNotes });
            toast({ title: 'Closed', description: 'Request closed successfully' });
            setCloseDialogOpen(false);
            setResolutionNotes('');
            fetchRequests();
        } catch (err) {
            toast({ title: 'Error', description: err.message || 'Failed to close', variant: 'destructive' });
        } finally {
            setClosing(false);
        }
    };

    // ── Delete ──

    const handleDelete = async () => {
        if (!deletingRequest) return;
        setDeleting(true);
        try {
            await api.deleteProjectRequest(projectId, deletingRequest.id);
            toast({ title: 'Deleted', description: 'Request deleted' });
            setDeleteDialogOpen(false);
            fetchRequests();
        } catch (err) {
            toast({ title: 'Error', description: err.message || 'Failed to delete', variant: 'destructive' });
        } finally {
            setDeleting(false);
        }
    };

    const isCreator = (req) => req.requestedBy === user?.id;
    const canEdit = (req) => !isProjectCompleted && isCreator(req) && req.status !== 'closed' && req.status !== 'resolved';
    const canDelete = (req) => !isProjectCompleted && isCreator(req);
    const canClose = (req) => !isProjectCompleted && req.status !== 'closed';

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                        <Filter className="w-4 h-4 mr-1" /> Filters
                    </Button>
                    {(filterType || filterStatus) && (
                        <Button variant="ghost" size="sm" onClick={() => { setFilterType(''); setFilterStatus(''); }}>
                            Clear
                        </Button>
                    )}
                </div>
                {!isProjectCompleted && (
                    <Button size="sm" onClick={openCreateDialog}>
                        <Plus className="w-4 h-4 mr-1" /> New Request
                    </Button>
                )}
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                    <div>
                        <Label className="text-xs">Type</Label>
                        <select className="block w-40 mt-1 rounded border px-2 py-1.5 text-sm"
                            value={filterType} onChange={e => setFilterType(e.target.value)}>
                            <option value="">All</option>
                            {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Status</Label>
                        <select className="block w-36 mt-1 rounded border px-2 py-1.5 text-sm"
                            value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                            <option value="">All</option>
                            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </select>
                    </div>
                </div>
            )}

            {/* Empty */}
            {requests.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p>No requests found</p>
                </div>
            )}

            {/* Request List */}
            {requests.map(req => (
                <Card key={req.id} className="overflow-hidden">
                    {/* Header row */}
                    <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedId(expandedId === req.id ? null : req.id)}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xs text-gray-400 font-mono shrink-0">{req.requestNumber}</span>
                            <Badge className={PRIORITY_COLORS[req.priority] || 'bg-gray-100'}>
                                {req.priority}
                            </Badge>
                            <Badge className={STATUS_COLORS[req.status] || 'bg-gray-100'}>
                                {STATUS_LABEL[req.status] || req.status}
                            </Badge>
                            <Badge variant="outline">{RequestTypeLabel[req.requestType] || req.requestType}</Badge>
                            <span className="text-sm font-medium truncate">{req.title}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs text-gray-400">
                                {format(new Date(req.createdAt), 'dd MMM yyyy')}
                            </span>
                            {expandedId === req.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                    </div>

                    {/* Expanded detail */}
                    {expandedId === req.id && (
                        <CardContent className="border-t pt-4 space-y-3">
                            <div>
                                <Label className="text-xs text-gray-500">Description</Label>
                                <p className="text-sm whitespace-pre-wrap">{req.description}</p>
                            </div>

                            <div className="flex gap-6 text-sm">
                                <div>
                                    <Label className="text-xs text-gray-500">Raised By</Label>
                                    <p>{req.requestedByName} <span className="text-xs text-gray-400">({req.requestedByRole})</span></p>
                                </div>
                                {req.resolvedByName && (
                                    <div>
                                        <Label className="text-xs text-gray-500">Closed By</Label>
                                        <p>{req.resolvedByName} {req.resolvedAt && <span className="text-xs text-gray-400">({format(new Date(req.resolvedAt), 'dd MMM yyyy HH:mm')})</span>}</p>
                                    </div>
                                )}
                            </div>

                            {req.resolutionNotes && (
                                <div>
                                    <Label className="text-xs text-gray-500">Resolution Notes</Label>
                                    <p className="text-sm whitespace-pre-wrap">{req.resolutionNotes}</p>
                                </div>
                            )}

                            {/* OEM fields */}
                            {(req.proposedCompletionDate || req.extendedHoursStart || req.requestedTeamMembers) && (
                                <div className="p-3 bg-amber-50 rounded-lg space-y-2">
                                    <Label className="text-xs font-medium text-amber-700">OEM Details</Label>
                                    {req.proposedCompletionDate && (
                                        <p className="text-sm"><strong>Proposed Completion:</strong> {format(new Date(req.proposedCompletionDate), 'dd MMM yyyy')}</p>
                                    )}
                                    {req.extendedHoursStart && req.extendedHoursEnd && (
                                        <p className="text-sm"><strong>Extended Hours:</strong> {req.extendedHoursStart} – {req.extendedHoursEnd}</p>
                                    )}
                                    {req.requestedTeamMembers && (
                                        <p className="text-sm"><strong>Requested Team Members:</strong> {req.requestedTeamMembers}</p>
                                    )}
                                </div>
                            )}

                            {/* Photos */}
                            {req.documents && req.documents.length > 0 && (
                                <div>
                                    <Label className="text-xs text-gray-500">Photos / Documents</Label>
                                    <div className="flex flex-wrap gap-2 mt-1">
                                        {req.documents.map(doc => (
                                            <a key={doc.id} href={doc.blobUrl} target="_blank" rel="noopener noreferrer"
                                                className="border rounded p-1 hover:shadow-md transition-shadow">
                                                {doc.mimeType?.startsWith('image/') ? (
                                                    <img src={doc.blobUrl} alt={doc.fileName}
                                                        className="w-20 h-20 object-cover rounded" />
                                                ) : (
                                                    <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded">
                                                        <Image className="w-6 h-6 text-gray-400" />
                                                    </div>
                                                )}
                                                <p className="text-[10px] text-gray-500 truncate w-20 mt-1">{doc.fileName}</p>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2 pt-2 border-t">
                                {canClose(req) && (
                                    <Button size="sm" variant="outline" className="text-green-600 border-green-300 hover:bg-green-50"
                                        onClick={(e) => { e.stopPropagation(); setClosingRequest(req); setResolutionNotes(''); setCloseDialogOpen(true); }}>
                                        <CheckCircle className="w-3.5 h-3.5 mr-1" /> Close
                                    </Button>
                                )}
                                {canEdit(req) && (
                                    <Button size="sm" variant="outline"
                                        onClick={(e) => { e.stopPropagation(); openEditDialog(req); }}>
                                        <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
                                    </Button>
                                )}
                                {canDelete(req) && (
                                    <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                                        onClick={(e) => { e.stopPropagation(); setDeletingRequest(req); setDeleteDialogOpen(true); }}>
                                        <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    )}
                </Card>
            ))}

            {/* ── Create / Edit Dialog ── */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-lg max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editingRequest ? 'Edit Request' : 'New Request'}</DialogTitle>
                        <DialogDescription>
                            {editingRequest ? 'Update the request details.' : 'Raise a new request or discrepancy for this project.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>Type</Label>
                                <select className="w-full rounded border px-2 py-2 text-sm mt-1"
                                    value={formData.requestType}
                                    onChange={e => setFormData(f => ({ ...f, requestType: e.target.value }))}
                                    disabled={!!editingRequest}>
                                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label>Priority</Label>
                                <select className="w-full rounded border px-2 py-2 text-sm mt-1"
                                    value={formData.priority}
                                    onChange={e => setFormData(f => ({ ...f, priority: e.target.value }))}>
                                    {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>

                        <div>
                            <Label>Title <span className="text-red-500">*</span></Label>
                            <Input className="mt-1" value={formData.title} maxLength={500}
                                onChange={e => setFormData(f => ({ ...f, title: e.target.value }))}
                                placeholder="Brief summary of the request" />
                        </div>

                        <div>
                            <Label>Description <span className="text-red-500">*</span></Label>
                            <textarea className="w-full rounded border px-3 py-2 text-sm mt-1 min-h-[100px]"
                                value={formData.description}
                                onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
                                placeholder="Detailed description..." />
                        </div>

                        {/* OEM-specific fields */}
                        {isOEM && (
                            <div className="p-3 bg-amber-50 rounded-lg space-y-3">
                                <Label className="text-xs font-medium text-amber-700">OEM Additional Details</Label>

                                <div>
                                    <Label className="text-xs">Proposed Revised Completion Date</Label>
                                    <Input type="date" className="mt-1" value={formData.proposedCompletionDate}
                                        onChange={e => setFormData(f => ({ ...f, proposedCompletionDate: e.target.value }))} />
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Extended Hours Start</Label>
                                        <Input type="time" className="mt-1" value={formData.extendedHoursStart}
                                            onChange={e => setFormData(f => ({ ...f, extendedHoursStart: e.target.value }))} />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Extended Hours End</Label>
                                        <Input type="time" className="mt-1" value={formData.extendedHoursEnd}
                                            onChange={e => setFormData(f => ({ ...f, extendedHoursEnd: e.target.value }))} />
                                    </div>
                                </div>

                                <div>
                                    <Label className="text-xs">Requested Team Members</Label>
                                    <textarea className="w-full rounded border px-3 py-2 text-sm mt-1 min-h-[60px]"
                                        value={formData.requestedTeamMembers}
                                        onChange={e => setFormData(f => ({ ...f, requestedTeamMembers: e.target.value }))}
                                        placeholder="Names or details of additional team members needed..." />
                                </div>
                            </div>
                        )}

                        {/* Photo upload */}
                        {!editingRequest && (
                            <div>
                                <Label>Photos / Documents</Label>
                                <div className="mt-1 border-2 border-dashed rounded-lg p-4 text-center">
                                    <input type="file" multiple accept="image/*,.pdf"
                                        className="hidden" id="request-file-input"
                                        onChange={e => setSelectedFiles(Array.from(e.target.files || []))} />
                                    <label htmlFor="request-file-input" className="cursor-pointer">
                                        <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                                        <p className="text-sm text-gray-500">Click to upload photos</p>
                                    </label>
                                    {selectedFiles.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {selectedFiles.map((f, i) => (
                                                <div key={i} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs">
                                                    {f.name}
                                                    <X className="w-3 h-3 cursor-pointer text-gray-500 hover:text-red-500"
                                                        onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleSubmit} disabled={submitting}>
                                {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                                {editingRequest ? 'Update' : 'Create'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Close Dialog ── */}
            <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Close Request</DialogTitle>
                        <DialogDescription>Close "{closingRequest?.title}"</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-2">
                        <div>
                            <Label>Resolution Notes (optional)</Label>
                            <textarea className="w-full rounded border px-3 py-2 text-sm mt-1 min-h-[80px]"
                                value={resolutionNotes}
                                onChange={e => setResolutionNotes(e.target.value)}
                                placeholder="How was this resolved..." />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleClose} disabled={closing}
                                className="bg-green-600 hover:bg-green-700">
                                {closing && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                                Close Request
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ── Delete Dialog ── */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Request</DialogTitle>
                        <DialogDescription>Are you sure you want to delete "{deletingRequest?.title}"?</DialogDescription>
                    </DialogHeader>
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
                            {deleting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
                            Delete
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ProjectRequests;
